import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import * as FiIcons from "react-icons/fi";
import * as CiIcons from "react-icons/ci";
import SafeIcon from "../common/SafeIcon";

const {
  FiArrowLeft, FiHome, FiMessageSquare, FiDownload, FiX, FiPlayCircle,
  FiMaximize2, FiUpload, FiLoader, FiSend, FiCheckCircle, FiClock,
  FiChevronRight, FiList, FiShield, FiInbox, FiImage, FiBriefcase,
  FiPlus, FiUsers, FiGrid, FiSettings, FiSearch, FiChevronDown, FiStar,
  FiMoreHorizontal, FiPhone, FiLink, FiSmile, FiPaperclip, FiFileText, FiDollarSign
} = FiIcons;

const { CiFileOn } = CiIcons;

const AdminCustomerDetails = () => {
  const { userId } = useParams();
  const { user, session } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [requests, setRequests] = useState([]);
  const [activeRequestId, setActiveRequestId] = useState(null);

  const [activeMedia, setActiveMedia] = useState([]);
  const [activeMessages, setActiveMessages] = useState([]);
  const [activeProgress, setActiveProgress] = useState([]);

  const [newMessage, setNewMessage] = useState("");
  const [role, setRole] = useState("admin");
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // New state variables for search, right panel tab, and invoices
  const [searchQuery, setSearchQuery] = useState("");
  const [rightPanelTab, setRightPanelTab] = useState("progress"); // "progress", "media", or "invoices"
  const [newStepTitle, setNewStepTitle] = useState("");
  const [newStepDesc, setNewStepDesc] = useState("");
  const [isAddingStep, setIsAddingStep] = useState(false);

  const [activeInvoices, setActiveInvoices] = useState([]);
  const [showRaiseInvoiceModal, setShowRaiseInvoiceModal] = useState(false);
  const [isRaisingInvoice, setIsRaisingInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    category: "Payment for Requested Service",
    title: "",
    totalFee: "",
    advancePaid: "",
    amount: "",
    currency: "USD",
    dueDate: "",
    description: ""
  });

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const activeRequest = requests.find(r => r.id === activeRequestId) || {};

  useEffect(() => {
    fetchInitialData();
    const handleReload = () => fetchCustomerInvoices();
    window.addEventListener('storage', handleReload);
    window.addEventListener('nlb_invoice_updated', handleReload);
    return () => {
      window.removeEventListener('storage', handleReload);
      window.removeEventListener('nlb_invoice_updated', handleReload);
    };
  }, [userId]);

  useEffect(() => { if (activeRequestId) fetchRequestSpecificData(activeRequestId); }, [activeRequestId]);

  const fetchCustomerInvoices = async () => {
    if (!userId) return;
    try {
      const { data: dbInv, error } = await supabase.from("invoices").select("*").eq("user_id", userId).order("created_at", { ascending: false });
      const local = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
      const localForUser = local.filter(inv => inv.user_id === userId);
      const combinedMap = new Map();
      if (!error && dbInv) dbInv.forEach(i => combinedMap.set(i.id, i));
      localForUser.forEach(i => {
        if (combinedMap.has(i.id)) {
          const existing = combinedMap.get(i.id);
          if (i.status === 'paid' || existing.status === 'paid') {
            combinedMap.set(i.id, { ...existing, ...i, status: 'paid', paid_at: i.paid_at || existing.paid_at });
          }
        } else {
          combinedMap.set(i.id, i);
        }
      });
      setActiveInvoices(Array.from(combinedMap.values()));
    } catch (err) {
      const local = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
      setActiveInvoices(local.filter(inv => inv.user_id === userId));
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", userId).single();
      setCustomer(profile);
      const { data: customerRequests } = await supabase.from("service_requests").select(`*, provider:providers(id, full_name)`).eq("user_id", userId).order("created_at", { ascending: false });
      setRequests(customerRequests || []);
      if (customerRequests?.length > 0) setActiveRequestId(customerRequests[0].id);
      await fetchCustomerInvoices();
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleRaiseInvoice = async () => {
    if (!invoiceForm.title.trim() || !invoiceForm.amount) {
      alert("Please enter invoice title and amount.");
      return;
    }
    setIsRaisingInvoice(true);
    try {
      const invNum = `INV-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const newInvoice = {
        id: crypto.randomUUID(),
        user_id: userId,
        user_email: customer?.email || null,
        request_id: activeRequestId || null,
        invoice_number: invNum,
        category: invoiceForm.category,
        title: invoiceForm.title.trim(),
        total_fee: invoiceForm.totalFee ? parseFloat(invoiceForm.totalFee) : null,
        advance_paid: invoiceForm.advancePaid ? parseFloat(invoiceForm.advancePaid) : null,
        amount: parseFloat(invoiceForm.amount),
        currency: invoiceForm.currency || "USD",
        status: "unpaid",
        due_date: invoiceForm.dueDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        description: invoiceForm.description.trim(),
        created_at: new Date().toISOString()
      };

      let savedData = null;
      try {
        const { data, error } = await supabase.from("invoices").insert([newInvoice]).select().single();
        if (!error && data) savedData = data;
      } catch (e) {
        console.warn("Fallback to local storage for raise invoice", e);
      }

      const finalInvoice = savedData || newInvoice;
      const local = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
      const existingIdx = local.findIndex(i => i.id === finalInvoice.id);
      if (existingIdx >= 0) local[existingIdx] = finalInvoice;
      else local.unshift(finalInvoice);
      localStorage.setItem('nlb_invoices', JSON.stringify(local));

      window.dispatchEvent(new Event('nlb_invoice_updated'));

      setActiveInvoices((prev) => [finalInvoice, ...prev.filter(i => i.id !== finalInvoice.id)]);
      setShowRaiseInvoiceModal(false);
      setInvoiceForm({ category: "Payment for Requested Service", title: "", totalFee: "", advancePaid: "", amount: "", currency: "USD", dueDate: "", description: "" });
      alert(`Invoice ${finalInvoice.invoice_number} raised successfully! It will now drop directly to ${customer?.full_name || 'customer'}'s dashboard.`);
    } catch (err) {
      alert("Failed to raise invoice: " + err.message);
    } finally {
      setIsRaisingInvoice(false);
    }
  };

  const fetchRequestSpecificData = async (requestId) => {
    try {
      const { data: mediaData } = await supabase.from("request_images").select("*").eq("request_id", requestId).order("created_at", { ascending: false });
      setActiveMedia(mediaData || []);
      const { data: msgData } = await supabase.from("request_messages").select("*").eq("request_id", requestId).order("created_at", { ascending: true });
      setActiveMessages(msgData || []);
      const { data: progressData } = await supabase.from("request_progress").select("*").eq("request_id", requestId).order("step_number", { ascending: true });
      setActiveProgress(progressData || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!activeRequestId) return;
    const chatChannel = supabase.channel(`chat_${activeRequestId}`).on("postgres_changes", { event: "INSERT", schema: "public", table: "request_messages", filter: `request_id=eq.${activeRequestId}` }, (payload) => setActiveMessages((prev) => [...prev, payload.new])).subscribe();
    return () => supabase.removeChannel(chatChannel);
  }, [activeRequestId]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [activeMessages]);

  const getMediaUrl = (url) => {
    if (!url) return "";
    if (url.includes("/functions/v1/upload-request-image")) {
      const token = session?.access_token || "";
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
      return `${url}&token=${encodeURIComponent(token)}&apikey=${encodeURIComponent(anonKey)}`;
    }
    return url;
  };

  const getFileExtension = (url) => {
    if (!url) return "";
    try {
      const urlObj = new URL(url);
      const key = urlObj.searchParams.get("key") || urlObj.pathname;
      return key.split(".").pop().toLowerCase();
    } catch {
      return url.split(".").pop().toLowerCase();
    }
  };

  const getFileType = (url) => {
    const ext = getFileExtension(url);
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return 'image';
    if (["mp4", "webm", "ogg", "mov", "m4v"].includes(ext)) return 'video';
    return 'document';
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !activeRequestId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("request_id", activeRequestId);
      const { data, error } = await supabase.functions.invoke("upload-request-image", { body: formData });
      if (error) throw error;
      const proxyUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-request-image?key=${encodeURIComponent(data.key)}`;
      const { data: inserted } = await supabase.from("request_images").insert([{ request_id: activeRequestId, image_url: proxyUrl, uploaded_by: user.id }]).select();
      if (inserted) setActiveMedia((prev) => [inserted[0], ...prev]);
    } catch (err) { alert("Upload failed: " + err.message); } finally { setUploading(false); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeRequestId) return;
    const msg = { request_id: activeRequestId, message: newMessage.trim(), sender_id: user.id, sender_role: 'admin', created_at: new Date().toISOString() };
    setNewMessage("");
    await supabase.from("request_messages").insert([msg]);
  };

  const addStep = async () => {
    if (!newStepTitle.trim() || !activeRequestId) return;
    try {
      const newEntry = {
        request_id: activeRequestId,
        title: newStepTitle.trim(),
        description: newStepDesc.trim(),
        step_number: activeProgress.length + 1,
        created_by: user.id,
        created_at: new Date().toISOString()
      };
      const { data, error } = await supabase.from("request_progress").insert([newEntry]).select();
      if (error) throw error;
      if (data) {
        setActiveProgress((prev) => [...prev, data[0]]);
        setNewStepTitle("");
        setNewStepDesc("");
        setIsAddingStep(false);
      }
    } catch (err) {
      alert("Failed to add progress step: " + err.message);
    }
  };

  // Filter requests based on search query
  const filteredRequests = requests.filter(req => {
    const serviceType = req.service_type?.toLowerCase() || "";
    const description = req.description?.toLowerCase() || "";
    const query = searchQuery.toLowerCase();
    return serviceType.includes(query) || description.includes(query);
  });

  if (loading) return <div className="h-screen flex items-center justify-center font-sans text-indigo-600 font-bold">Loading Workspace...</div>;

  return (
    <div className="flex h-screen bg-[#F8FAFC] font-sans text-gray-900 overflow-hidden">

      {/* 1. TICKET LIST SIDEBAR */}
      <aside className="w-80 bg-white border-r border-gray-100 flex flex-col h-full shrink-0">
        {/* Header section with Back Button and Logo */}
        <div className="p-5 border-b border-gray-100 flex flex-col gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-[#C89B3C] transition-colors py-1 focus:outline-none"
          >
            <FiArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </button>

          <div className="flex items-center justify-center py-2">
            <img
              src="/images/NRI_Logo.svg"
              alt="NRI Local Buddy Logo"
              className="h-12 w-auto object-contain"
            />
          </div>
        </div>

        {/* Scrollable ticket list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filteredRequests.length > 0 ? (
            filteredRequests.map((req) => {
              const isActive = activeRequestId === req.id;
              const serviceType = req.service_type?.replace(/-/g, " ");
              const dateObj = new Date(req.created_at);
              const formattedTime = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

              return (
                <div
                  key={req.id}
                  onClick={() => setActiveRequestId(req.id)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all cursor-pointer flex flex-col gap-1.5 relative ${isActive
                      ? 'bg-gray-50 border-gray-100 shadow-sm'
                      : 'bg-white border-transparent hover:bg-gray-50/50'
                    }`}
                >
                  {/* Row 1: Customer Name and Time */}
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="font-heading font-bold text-gray-900 text-base truncate">
                      {customer?.full_name || "Client"}
                    </span>
                    <span className="text-xs text-[#A0AEC0] font-semibold shrink-0">
                      {formattedTime}
                    </span>
                  </div>

                  {/* Row 2: Service Request Title */}
                  <div className="text-sm text-gray-600 truncate capitalize font-medium">
                    {serviceType}
                  </div>

                  {/* Row 3: Status Tag and Active dot */}
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${req.status === 'completed'
                        ? 'bg-emerald-50 text-[#0E8A6A]'
                        : req.status === 'pending'
                          ? 'bg-amber-50 text-amber-600'
                          : 'bg-blue-50 text-[#0F2D52]'
                      }`}>
                      {req.status}
                    </span>

                    {/* Badge indicator */}
                    <div className="flex items-center gap-1.5">
                      {isActive && (
                        <div className="w-2 h-2 rounded-full bg-[#C89B3C]" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-12 text-center text-gray-400 text-xs font-medium">
              No tickets found
            </div>
          )}
        </div>
      </aside>

      {/* 2. CASE CHAT CENTRAL PANEL */}
      <section className="flex-1 flex flex-col h-full bg-[#F8FAFC]">
        {/* Chat Header */}
        <div className="h-16 border-b border-gray-100 bg-white px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-heading font-bold text-gray-900 text-lg capitalize">
              {activeRequest.service_type?.replace(/-/g, " ")}
            </span>
          </div>

          {/* Prominent Raise Invoice Action Button */}
          <button
            onClick={() => {
              if (activeRequest.service_type) {
                setInvoiceForm(prev => ({ ...prev, title: `${activeRequest.service_type.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Fee` }));
              }
              setShowRaiseInvoiceModal(true);
            }}
            className="bg-gradient-to-r from-[#0F2D52] to-[#1e4473] hover:from-[#0a1e36] hover:to-[#0F2D52] text-white font-bold text-xs px-4 py-2 rounded-xl shadow-md shadow-blue-950/10 hover:shadow-lg transition-all flex items-center gap-1.5 active:scale-95 border border-blue-800/30"
          >
            <FiPlus size={14} className="text-[#C89B3C]" />
            <span>Raise Invoice</span>
          </button>
        </div>

        {/* Chat Feed */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Welcome Info Message */}
          <div className="flex justify-center my-2">
            <div className="bg-[#f0f4f9] text-[#0F2D52] text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 shadow-sm border border-[#dbe6f2]">
              <FiMessageSquare size={13} />
              <span>Connected to {customer?.full_name || 'Client'}. Feel free to exchange messages.</span>
            </div>
          </div>

          {/* System Notification: Provider connected info */}
          {activeRequest.provider && (
            <div className="flex items-center justify-center my-4">
              <div className="flex items-center gap-2 text-[11px] text-gray-400 bg-white border border-gray-100 rounded-full px-3.5 py-1 shadow-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0E8A6A] animate-pulse" />
                <span className="font-semibold text-gray-600">{activeRequest.provider.full_name}</span>
                <span>has connected to take over the request.</span>
              </div>
            </div>
          )}

          {/* Render Active Messages */}
          {activeMessages.length > 0 ? (
            activeMessages.map((msg) => {
              const isMe = msg.sender_id === user.id;
              const dateObj = new Date(msg.created_at);
              const formattedTime = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

              if (isMe) {
                return (
                  <div key={msg.id} className="flex justify-end gap-3 max-w-[85%] ml-auto">
                    <div className="flex flex-col items-end gap-1">
                      <div className="bg-[#f0f4f9] text-[#0F2D52] p-4 rounded-[20px] rounded-tr-none border border-[#dbe6f2] shadow-sm max-w-md">
                        <p className="text-xs leading-relaxed font-medium whitespace-pre-wrap">{msg.message}</p>
                      </div>
                      <span className="text-[9px] text-gray-400 font-semibold tracking-wide">
                        {formattedTime} via Portal
                      </span>
                    </div>
                  </div>
                );
              } else {
                const initial = customer?.full_name?.charAt(0).toUpperCase() || 'C';
                return (
                  <div key={msg.id} className="flex gap-3 max-w-[85%] mr-auto">
                    {/* Customer Avatar */}
                    <div className="w-8 h-8 rounded-full bg-[#0E8A6A] text-white flex items-center justify-center text-xs font-bold shadow-sm shrink-0 mt-1">
                      {initial}
                    </div>

                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-heading text-[11px] font-bold text-gray-700">{customer?.full_name || 'Client'}</span>
                        <span className="text-[9px] text-gray-400">{formattedTime} via WhatsApp</span>
                      </div>
                      <div className="bg-white text-gray-800 p-4 rounded-[20px] rounded-tl-none border border-gray-100 shadow-sm max-w-md">
                        <p className="text-xs leading-relaxed font-medium whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    </div>
                  </div>
                );
              }
            })
          ) : (
            <div className="h-[60%] flex flex-col items-center justify-center text-gray-300 gap-3">
              <div className="p-4 bg-white rounded-full shadow-sm border border-gray-50">
                <FiInbox size={32} className="text-gray-300" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Start the conversation</p>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Chat Input Area */}
        <div className="p-4 bg-white border-t border-gray-100 flex flex-col gap-3">
          {/* Top meta-options */}
          <div className="flex items-center justify-between text-xs px-2">
            <span className="text-gray-400 font-semibold">
              From: <span className="text-gray-700 font-bold">{user?.email?.split('@')[0]}</span>
            </span>
          </div>

          {/* Text Area and Buttons Wrapper */}
          <div className="border border-gray-100 rounded-2xl bg-gray-50/50 p-2.5 focus-within:border-gold-300 focus-within:bg-white transition-all flex flex-col gap-2">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={`Message ${customer?.full_name || 'client'}...`}
              rows={2}
              className="w-full bg-transparent border-none text-xs font-medium text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-0 p-2 resize-none leading-relaxed"
            />

            {/* Bottom Actions Row */}
            <div className="flex items-center justify-between border-t border-gray-100/80 pt-2 px-1">
              <div className="flex items-center gap-1 text-gray-400">
                <button className="p-2 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100/50" title="Text formatting">
                  <span className="font-bold text-xs">A</span>
                </button>

                {/* File Attachment Hook */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:text-[#C89B3C] transition-colors rounded-lg hover:bg-gold-50 relative"
                  title="Upload attachment"
                  disabled={uploading}
                >
                  {uploading ? (
                    <FiLoader className="animate-spin" size={15} />
                  ) : (
                    <FiPaperclip size={15} />
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />

                <button className="p-2 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100/50" title="Insert link">
                  <FiLink size={15} />
                </button>
                <button className="p-2 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100/50" title="Emojis">
                  <FiSmile size={15} />
                </button>
              </div>

              {/* Send Button */}
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim()}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${newMessage.trim()
                    ? 'bg-[#C89B3C] hover:bg-[#ab812f] text-white shadow-md shadow-gold-100 hover:scale-105'
                    : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
              >
                <FiSend size={15} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. UTILITY PANEL (RIGHTMOST KNOWLEDGE BASE LAYOUT) */}
      <aside className="w-96 bg-white border-l border-gray-100 flex flex-col h-full">
        {/* Header */}
        <div className="p-5 border-b border-gray-100">
          <h2 className="font-heading font-bold text-gray-900 text-sm tracking-wide mb-4">Request Hub</h2>

          {/* Triple Tabs for Work Progress, Media, vs Invoices */}
          <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100/80">
            <button
              onClick={() => setRightPanelTab("progress")}
              className={`flex-1 text-center py-2 text-[11px] font-bold rounded-lg transition-all ${rightPanelTab === "progress"
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-100/50'
                  : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              Progress
            </button>
            <button
              onClick={() => setRightPanelTab("media")}
              className={`flex-1 text-center py-2 text-[11px] font-bold rounded-lg transition-all ${rightPanelTab === "media"
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-100/50'
                  : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              Media ({activeMedia.length})
            </button>
            <button
              onClick={() => setRightPanelTab("invoices")}
              className={`flex-1 text-center py-2 text-[11px] font-bold rounded-lg transition-all ${rightPanelTab === "invoices"
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-100/50'
                  : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              Invoices ({activeInvoices.length})
            </button>
          </div>
        </div>

        {/* Tab Content Panel */}
        <div className="flex-1 overflow-y-auto p-4">
          {rightPanelTab === "progress" ? (
            <div className="space-y-4">
              {/* Add Step Button & Form block */}
              <div className="border-b border-gray-100 pb-4 mb-2">
                {!isAddingStep ? (
                  <button
                    onClick={() => setIsAddingStep(true)}
                    className="w-full py-2.5 px-4 bg-[#C89B3C] hover:bg-[#ab812f] text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-sm shadow-gold-100"
                  >
                    <FiPlus size={14} />
                    <span>Add Progress Log</span>
                  </button>
                ) : (
                  <div className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">New Progress Log</span>
                      <button
                        onClick={() => { setIsAddingStep(false); setNewStepTitle(""); setNewStepDesc(""); }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FiX size={14} />
                      </button>
                    </div>

                    <input
                      type="text"
                      placeholder="Step Title (e.g. Flight Booked)"
                      value={newStepTitle}
                      onChange={(e) => setNewStepTitle(e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gold-300 focus:ring-1 focus:ring-gold-300"
                    />

                    <textarea
                      placeholder="Step Description (e.g. Flight AI902 details sent)"
                      value={newStepDesc}
                      onChange={(e) => setNewStepDesc(e.target.value)}
                      rows={2}
                      className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gold-300 focus:ring-1 focus:ring-gold-300 resize-none"
                    />

                    <button
                      onClick={addStep}
                      disabled={!newStepTitle.trim()}
                      className={`w-full py-2 rounded-xl text-xs font-bold transition-all ${newStepTitle.trim()
                          ? 'bg-[#0F2D52] hover:bg-[#0a1e36] text-white shadow-sm'
                          : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        }`}
                    >
                      Save Step
                    </button>
                  </div>
                )}
              </div>

              {activeProgress.length > 0 ? (
                activeProgress.map((step, idx) => {
                  const palettes = [
                    { bg: 'bg-rose-50 border-rose-100', text: 'text-rose-500' },
                    { bg: 'bg-gold-50 border-gold-100 text-[#C89B3C]' },
                    { bg: 'bg-blue-50 border-blue-100 text-[#0F2D52]' },
                    { bg: 'bg-emerald-50 border-emerald-100 text-[#0E8A6A]' },
                  ];
                  const scheme = palettes[idx % palettes.length];

                  return (
                    <div
                      key={step.id}
                      className="bg-white border border-gray-100 rounded-2xl p-4 flex gap-4 hover:border-gray-200 hover:shadow-sm transition-all"
                    >
                      {/* Left color icon bubble */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${scheme.bg} border`}>
                        <FiCheckCircle className={scheme.text} size={18} />
                      </div>

                      {/* Content column */}
                      <div className="flex-1 flex flex-col gap-1 overflow-hidden">
                        <div className="font-heading font-bold text-gray-800 text-xs truncate">
                          {step.title}
                        </div>
                        <p className="text-gray-500 text-[11px] leading-relaxed">
                          {step.description}
                        </p>

                        <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-50">
                          <span className="text-[9px] bg-gray-50 text-gray-400 font-bold px-2 py-0.5 rounded uppercase">
                            Step {step.step_number || (idx + 1)}
                          </span>
                          <span className="text-[9px] text-gray-400 font-semibold">
                            {new Date(step.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-16 flex flex-col items-center justify-center text-gray-300 gap-2 opacity-50">
                  <FiList size={30} />
                  <p className="text-xs font-bold uppercase tracking-wider">No progress logs found</p>
                </div>
              )}
            </div>
          ) : rightPanelTab === "media" ? (
            // Media/Attachments Tab
            <div className="space-y-6">
              {/* Upload control header */}
              <div className="flex justify-between items-center">
                <span className="font-heading text-xs font-bold text-gray-500">Files Vault</span>
                <label className="cursor-pointer bg-[#C89B3C] hover:bg-[#ab812f] text-white px-3.5 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-all shadow-sm shadow-gold-100 active:scale-95 select-none">
                  {uploading ? (
                    <FiLoader className="animate-spin" size={13} />
                  ) : (
                    <FiUpload size={13} />
                  )}
                  <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} disabled={uploading} />
                  <span>{uploading ? "Uploading..." : "Upload New"}</span>
                </label>
              </div>

              {/* Grid representation */}
              {activeMedia.length > 0 ? (
                <div className="grid grid-cols-2 gap-3.5">
                  {activeMedia.map((m) => {
                    const type = getFileType(m.image_url);
                    return (
                      <div
                        key={m.id}
                        onClick={() => { setSelectedMedia(m); setIsMediaModalOpen(true); }}
                        className="aspect-square bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 hover:border-gold-300 cursor-pointer group flex flex-col items-center justify-center relative shadow-sm hover:shadow-md transition-all"
                      >
                        {type === 'image' ? (
                          <img
                            src={getMediaUrl(m.image_url)}
                            alt="attachment"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`flex flex-col items-center gap-1.5 ${type === 'image' ? 'hidden' : 'flex'}`}>
                          <SafeIcon icon={type === 'video' ? FiPlayCircle : CiFileOn} className="text-gray-300 text-3xl group-hover:text-[#C89B3C] transition-colors" />
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{type}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-20 border border-dashed border-gray-150 rounded-2xl flex flex-col items-center justify-center text-gray-300 gap-3">
                  <FiImage size={36} className="opacity-40" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 opacity-60">No media uploaded</p>
                </div>
              )}
            </div>
          ) : (
            // Invoices Tab
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                <span className="font-heading text-xs font-bold text-gray-500">Raised Invoices</span>
                <button
                  onClick={() => setShowRaiseInvoiceModal(true)}
                  className="bg-[#0F2D52] hover:bg-[#0a1e36] text-white px-3 py-1.5 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-all shadow-sm active:scale-95 select-none"
                >
                  <FiPlus size={13} className="text-[#C89B3C]" />
                  <span>Raise Invoice</span>
                </button>
              </div>

              {activeInvoices.length > 0 ? (
                activeInvoices.map((inv) => (
                  <div key={inv.id} className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2 hover:shadow-sm transition-all">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-wider">
                          {inv.invoice_number}
                        </span>
                        <h4 className="font-bold text-gray-900 text-xs mt-1">{inv.title}</h4>
                      </div>
                      <span className={`text-[9px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                        inv.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                      }`}>
                        {inv.status}
                      </span>
                    </div>

                    {inv.description && (
                      <p className="text-[11px] text-gray-500 line-clamp-2">{inv.description}</p>
                    )}

                    <div className="flex justify-between items-center pt-2 border-t border-gray-50 text-xs">
                      <span className="font-black text-gray-900 text-sm">
                        {inv.currency === 'INR' ? '₹' : '$'}{inv.amount}
                      </span>
                      <span className="text-[10px] text-gray-400 font-semibold">
                        Due: {new Date(inv.due_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-16 flex flex-col items-center justify-center text-gray-300 gap-2 opacity-60">
                  <FiFileText size={32} />
                  <p className="text-xs font-bold uppercase tracking-wider">No invoices raised yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* MEDIA MODAL POPUP */}
      {isMediaModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white">
              <h3 className="font-heading font-bold text-base text-gray-900">{selectedMedia ? 'Media Preview' : 'Attachments Vault'}</h3>
              <div className="flex gap-2">
                {selectedMedia && (
                  <button onClick={() => setSelectedMedia(null)} className="px-4 py-2 text-xs font-bold text-[#C89B3C] hover:bg-gold-50 rounded-xl transition-all">
                    All Files
                  </button>
                )}
                <button onClick={() => setIsMediaModalOpen(false)} className="p-2 bg-gray-100 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all">
                  <FiX size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-8 bg-gray-50/50 flex flex-col items-center justify-center">
              {selectedMedia ? (
                <div className="w-full flex flex-col items-center justify-center gap-6">
                  <div className="w-full max-w-3xl bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {getFileType(selectedMedia.image_url) === 'image' ? (
                      <div className="p-4 flex items-center justify-center max-h-[50vh] w-full">
                        <img src={getMediaUrl(selectedMedia.image_url)} alt="Preview" className="max-w-full max-h-[45vh] object-contain rounded-xl" />
                      </div>
                    ) : getFileType(selectedMedia.image_url) === 'video' ? (
                      <div className="p-4 flex items-center justify-center max-h-[50vh] w-full">
                        <video src={getMediaUrl(selectedMedia.image_url)} controls autoPlay className="max-w-full max-h-[45vh] rounded-xl" />
                      </div>
                    ) : getFileExtension(selectedMedia.image_url) === 'pdf' ? (
                      <iframe
                        src={getMediaUrl(selectedMedia.image_url)}
                        className="w-full h-[55vh] border-0"
                        title="PDF Document Preview"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-3 py-16 px-20">
                        <CiFileOn className="text-gray-200 text-7xl" />
                        <p className="text-gray-400 font-bold uppercase tracking-wider text-xs">
                          {getFileExtension(selectedMedia.image_url) || 'Document'} File
                        </p>
                      </div>
                    )}
                  </div>
                  <a
                    href={getMediaUrl(selectedMedia.image_url)}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 bg-[#C89B3C] hover:bg-[#ab812f] text-white px-8 py-3.5 rounded-xl font-bold uppercase text-xs tracking-wider transition-all shadow-lg shadow-gold-100 hover:scale-102"
                  >
                    <FiDownload size={14} />
                    <span>Download Attachment</span>
                  </a>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 w-full">
                  {activeMedia.map((m) => (
                    <div
                      key={m.id}
                      className="cursor-pointer group aspect-square bg-white rounded-2xl overflow-hidden border border-gold-300 hover:shadow-md flex items-center justify-center transition-all relative"
                      onClick={() => setSelectedMedia(m)}
                    >
                      {getFileType(m.image_url) === 'image' ? (
                        <img src={getMediaUrl(m.image_url)} alt="vault thumbnail" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      ) : (
                        <SafeIcon icon={getFileType(m.image_url) === 'video' ? FiPlayCircle : CiFileOn} className="text-gray-200 text-5xl group-hover:text-gold-500 transition-colors" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STUNNING, PROFESSIONAL & RESPONSIVE RAISE INVOICE MODAL POPUP */}
      {showRaiseInvoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-3 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-[95%] sm:w-full sm:max-w-xl shadow-2xl relative max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
            
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50/80 via-white to-gold-50/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#0F2D52] to-[#1e4473] text-white flex items-center justify-center shadow-md shadow-blue-900/10">
                  <FiFileText className="text-[#C89B3C] text-xl" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#0F2D52] tracking-wide">Raise Customer Invoice</h3>
                  <p className="text-xs text-gray-500 font-medium">
                    Drops directly to <span className="font-bold text-gray-800">{customer?.full_name || 'customer'}</span>'s dashboard
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowRaiseInvoiceModal(false)} 
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-50 text-gray-400 hover:text-red-600 flex items-center justify-center transition-colors"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
              
              {/* Invoice Category / Purpose */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Invoice Purpose / Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={invoiceForm.category}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, category: e.target.value })}
                    className="w-full border-2 border-gold-300/60 rounded-2xl px-4 py-3 text-xs font-bold text-[#0F2D52] bg-gold-50/20 focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="Advance Payment">⚡ Advance Payment</option>
                    <option value="Payment for Requested Service">💼 Payment for Requested Service</option>
                    <option value="Pending Amount">⏳ Pending Amount Settlement</option>
                    <option value="Other">📌 Other Service Charges</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                    <FiChevronDown size={16} />
                  </div>
                </div>
              </div>

              {/* Title Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Invoice Title / Item Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Property Care Monthly Inspection Fee"
                  value={invoiceForm.title}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, title: e.target.value })}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-xs font-medium text-gray-800 focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none transition-all"
                />
              </div>

              {/* Fee Breakdown Optional Grid Card */}
              <div className="bg-gradient-to-br from-gray-50 to-gold-50/20 border border-gold-200/60 rounded-2xl p-4 space-y-3">
                <span className="text-[11px] font-bold text-[#0F2D52] uppercase tracking-wider block">Fee Breakdown (Optional)</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Total Service Fee</label>
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      value={invoiceForm.totalFee}
                      onChange={(e) => {
                        const tf = e.target.value;
                        const adv = invoiceForm.advancePaid || 0;
                        const calc = tf ? Math.max(0, parseFloat(tf) - parseFloat(adv)) : "";
                        setInvoiceForm(prev => ({ ...prev, totalFee: tf, amount: calc !== "" ? calc.toString() : prev.amount }));
                      }}
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-gray-800 bg-white focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Advance Paid</label>
                    <input
                      type="number"
                      placeholder="e.g. 200"
                      value={invoiceForm.advancePaid}
                      onChange={(e) => {
                        const adv = e.target.value;
                        const tf = invoiceForm.totalFee || 0;
                        const calc = tf ? Math.max(0, parseFloat(tf) - parseFloat(adv || 0)) : "";
                        setInvoiceForm(prev => ({ ...prev, advancePaid: adv, amount: calc !== "" ? calc.toString() : prev.amount }));
                      }}
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-gray-800 bg-white focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Amount Due & Currency */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-[#0F2D52] uppercase tracking-wider mb-1.5">
                    Net Payable Amount Due <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="250"
                      value={invoiceForm.amount}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                      className="w-full border-2 border-[#0F2D52] rounded-2xl px-4 py-3 text-sm font-black text-gray-900 bg-blue-50/30 focus:ring-2 focus:ring-[#0F2D52]/20 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Currency</label>
                  <select
                    value={invoiceForm.currency}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, currency: e.target.value })}
                    className="w-full border border-gray-200 rounded-2xl px-3.5 py-3 text-xs font-bold text-gray-800 focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none bg-white cursor-pointer"
                  >
                    <option value="USD">$ USD</option>
                    <option value="INR">₹ INR</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                  </select>
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Payment Due Date</label>
                <input
                  type="date"
                  value={invoiceForm.dueDate}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-xs font-medium text-gray-800 focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none"
                />
              </div>

              {/* Description Notes */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Description / Payment Notes</label>
                <textarea
                  rows={2}
                  placeholder="Provide additional payment instructions or notes for the customer..."
                  value={invoiceForm.description}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-xs font-medium text-gray-800 focus:border-[#C89B3C] focus:ring-2 focus:ring-[#C89B3C]/20 focus:outline-none resize-none"
                />
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50/80 flex items-center justify-end gap-3 shrink-0">
              <button 
                onClick={() => setShowRaiseInvoiceModal(false)} 
                className="px-5 py-2.5 text-xs font-bold text-gray-600 hover:bg-gray-200/60 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseInvoice}
                disabled={isRaisingInvoice || !invoiceForm.title.trim() || !invoiceForm.amount}
                className="bg-gradient-to-r from-[#0F2D52] to-[#1e4473] hover:from-[#0a1e36] hover:to-[#0F2D52] disabled:opacity-50 text-white px-6 py-3 rounded-xl text-xs font-bold shadow-lg shadow-blue-900/10 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 border border-blue-800/30"
              >
                {isRaisingInvoice ? <FiLoader className="animate-spin" size={16} /> : <FiSend className="text-[#C89B3C]" size={16} />}
                <span>Raise & Drop to Dashboard</span>
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomerDetails;